/**
 * Vue Router 4 represents zero-or-more repeatable params (`:slugs*`) as `""`
 * when absent, not `[]`. Normalize before passing to components or router.push.
 */
export function toSlugsArray(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((s) => s.toString())
  }

  if (raw && raw !== '') {
    return [raw.toString()]
  }

  return []
}

export function normalizeSearchRouteParams(
  params: Record<string, unknown>,
): Record<string, string | string[]> {
  return {
    ...params,
    slugs: toSlugsArray(params.slugs),
  }
}

/**
 * Last segment looks like a static file when it contains a dot
 * (`/favicon.ico`, `/sw.js`, `/assets/foo.abc123.js`, `/img/logo.svg`). We
 * never want to append a trailing slash to those.
 */
const STATIC_FILE_SEGMENT_RE = /\.[^/]+$/

/**
 * Split a URL string into its path portion and the rest (query + hash).
 * Accepts a raw path (`/foo`), a path with query (`/foo?bar=baz`), a path
 * with hash (`/foo#x`) or any combination.
 */
function splitUrl(url: string): { path: string; rest: string } {
  const candidates = [url.indexOf('?'), url.indexOf('#')].filter(
    (i) => i !== -1,
  )

  if (candidates.length === 0) {
    return { path: url, rest: '' }
  }

  const splitAt = Math.min(...candidates)
  return { path: url.slice(0, splitAt), rest: url.slice(splitAt) }
}

/**
 * Decide whether `url` should be canonicalized to its trailing-slash form.
 * Only the **path** portion is inspected — query string and hash are
 * ignored (so `/foo/?bar=baz` correctly returns `false`).
 *
 * Returns false for:
 * - empty input
 * - root `/`
 * - paths that already end in `/`
 * - paths whose last segment looks like a static asset
 */
export function shouldAddTrailingSlash(url: string): boolean {
  const { path } = splitUrl(url)

  if (!path || path === '/' || path.endsWith('/')) return false

  const lastSegment = path.slice(path.lastIndexOf('/') + 1)
  if (STATIC_FILE_SEGMENT_RE.test(lastSegment)) return false

  return true
}

/**
 * Return `url` with a trailing slash appended to its path portion, while
 * preserving any query string or hash. Idempotent — calling it on a URL
 * that already ends in `/` (before the `?`/`#`) returns the same string.
 *
 * Examples:
 *   `/foo`            -> `/foo/`
 *   `/foo/`           -> `/foo/`
 *   `/foo?bar=baz`    -> `/foo/?bar=baz`
 *   `/foo/?bar=baz`   -> `/foo/?bar=baz`
 *   `/foo#section`    -> `/foo/#section`
 */
export function toTrailingSlashUrl(url: string): string {
  const { path, rest } = splitUrl(url)
  const normalizedPath = path.endsWith('/') ? path : `${path}/`
  return `${normalizedPath}${rest}`
}

export interface ParsedRouteUrl {
  path: string
  query: Record<string, string>
  hash: string
}

/**
 * Parse a path-with-optional-query-and-hash string (`/foo/?a=b#x`) into a
 * structured `{ path, query, hash }` shape that can be handed straight to
 * vue-router's `RouteLocationRaw` fields.
 *
 * Why this exists: passing `{ path: '/foo/?a=b' }` to `router.push` /
 * `router.replace` silently drops the query string — vue-router only reads
 * the path part of the `path:` field. So when we want to redirect to a URL
 * that may carry a query or hash, we split it up first and pass the parts
 * separately.
 */
export function parseRouteUrl(url: string): ParsedRouteUrl {
  const { path: pathWithRest, rest } = splitUrl(url)

  const hashIdx = rest.indexOf('#')
  const hash = hashIdx === -1 ? '' : rest.slice(hashIdx)
  const queryStr = hashIdx === -1 ? rest : rest.slice(0, hashIdx)

  const query: Record<string, string> = {}
  if (queryStr.startsWith('?')) {
    new URLSearchParams(queryStr.slice(1)).forEach((value, key) => {
      query[key] = value
    })
  }

  return { hash, path: pathWithRest, query }
}
